import React from 'react';
import type { BookingDetail } from '../../../types';

interface BookingClientHistoryProps {
  clientHistory: BookingDetail['client_history'];
  onBookingClick?: (bookingId: string) => void;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'confirmed':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'pending':
      return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    case 'completed':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'cancelled':
      return 'bg-red-50 text-red-700 border-red-200';
    case 'no_show':
      return 'bg-gray-50 text-gray-700 border-gray-200';
    default:
      return 'bg-gray-50 text-gray-700 border-gray-200';
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'confirmed':
      return (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      );
    case 'completed':
      return (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      );
    default:
      return null;
  }
};

export const BookingClientHistory: React.FC<BookingClientHistoryProps> = ({
  clientHistory,
  onBookingClick,
}) => {
  if (!clientHistory) {
    return null;
  }

  const isFirstTimeClient = clientHistory.total_bookings <= 1;

  return (
    <div className="mx-4 mb-4 p-4 bg-white border border-gray-200 rounded-xl">
      <div className="mb-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
          Client History
        </h3>
        <div className="text-sm text-gray-600 flex items-center gap-2">
          {isFirstTimeClient ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
              First Visit
            </span>
          ) : (
            <>
              <span className="font-medium">{clientHistory.total_bookings} bookings</span>
              {clientHistory.total_spent_formatted && (
                <>
                  <span className="text-gray-400">•</span>
                  <span className="font-medium">{clientHistory.total_spent_formatted} total</span>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Previous and Next Bookings */}
      <div className="space-y-2">
        {/* Previous Booking */}
        {clientHistory.previous_booking ? (
          <button
            onClick={() => onBookingClick?.(clientHistory.previous_booking!.id)}
            className="w-full text-left bg-gray-50 rounded-lg p-3 hover:bg-gray-100 active:bg-gray-150 transition-colors"
          >
            <div className="flex items-start justify-between mb-1">
              <span className="text-xs font-medium text-gray-500 flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Previous Booking
              </span>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded border ${getStatusColor(clientHistory.previous_booking.status)}`}>
                {getStatusIcon(clientHistory.previous_booking.status)}
                {clientHistory.previous_booking.status.replace('_', ' ')}
              </span>
            </div>
            <div className="text-sm font-semibold text-gray-900">
              {clientHistory.previous_booking.date_formatted}
            </div>
            <div className="text-sm text-gray-700">
              {clientHistory.previous_booking.service_name}
            </div>
          </button>
        ) : (
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <span className="text-xs text-gray-500">No previous bookings</span>
          </div>
        )}

        {/* Next Booking */}
        {clientHistory.next_booking ? (
          <button
            onClick={() => onBookingClick?.(clientHistory.next_booking!.id)}
            className="w-full text-left bg-gray-50 rounded-lg p-3 hover:bg-gray-100 active:bg-gray-150 transition-colors"
          >
            <div className="flex items-start justify-between mb-1">
              <span className="text-xs font-medium text-gray-500 flex items-center gap-1">
                Next Booking
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </span>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded border ${getStatusColor(clientHistory.next_booking.status)}`}>
                {getStatusIcon(clientHistory.next_booking.status)}
                {clientHistory.next_booking.status.replace('_', ' ')}
              </span>
            </div>
            <div className="text-sm font-semibold text-gray-900">
              {clientHistory.next_booking.date_formatted}
            </div>
            <div className="text-sm text-gray-700">
              {clientHistory.next_booking.service_name}
            </div>
          </button>
        ) : (
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <span className="text-xs text-gray-500">No upcoming bookings</span>
          </div>
        )}
      </div>
    </div>
  );
};
